import { useMemo, useState, useCallback, useEffect } from 'react';
import { Box, Button, TextField, Paper, Typography, Stack, Divider } from '@mui/material';
import GlobalWrapper from '../../../../components/Wrapper/GlobalWrapper';
import GlobalDialog from '../../../../components/mui/Dialogue/GlobalDialog';
import StudentSyncModal from './components/StudentSyncModal';
import CourseSyncModal from './components/CourseSyncModal';
import TutorSyncModal from './components/TutorSyncModal';
import ConfirmAllSyncModal from './components/ConfirmAllSyncModal';
import UserSyncModal from './components/UserSyncModal';
import GlobalTable from '../../../../components/CustomTableWithPagination/GlobalTable';
import { LensSyncTableHeader } from './components/lensSyncTableHeader';
import { useSelector } from 'react-redux';
import { useDispatch } from 'react-redux';
import { getLensSyncData } from '../../../../redux/features/lensSyncSlice';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';

export default function StudentSync() {
  const { data, meta, loading } = useSelector((state) => state.lens.lensSyncTable);
  const dispatch = useDispatch();
  const [selectedUser, setSelectedUser] = useState(null);
  const [tableState, setTableState] = useState({
    page: 1,
    pageSize: 10,
    search: '',
    frozenColumns: [],
    hiddenColumns: [],
  });

  const dispatchParams = useMemo(
    () => ({
      page: tableState.page,
      limit: tableState.pageSize,
      search: tableState.search || '',
    }),
    [tableState.page, tableState.pageSize, tableState.search],
  );

  useEffect(() => {
    dispatch(getLensSyncData(dispatchParams));
  }, [dispatch, dispatchParams]);

  //refersh logic + close
  const closeModal = useCallback((apiRecall = true) => {
    console.log('shouldRefresh', apiRecall);

    setModalState({ actionKey: '', row: null });
    if (apiRecall) {
      dispatch(getLensSyncData(dispatchParams));
    }
  }, []);

  const [modalState, setModalState] = useState({
    actionKey: '',
    row: null,
  });

  const handleButtonActionClick = useCallback((actionKey, row) => {
    if (!actionKey || !row) return;

    setModalState({
      actionKey,
      row,
    });
  }, []);

  /* -----------------------------------------
      MODAL JSON
  ------------------------------------------ */
  const modalConfig = useMemo(
    () => [
      {
        actionKey: 'studentSync',
        title: 'Student Sync',
        render: ({ row }) => <StudentSyncModal row={row} onClose={closeModal} />,
      },
      {
        actionKey: 'courseSync',
        title: 'Course Sync',
        render: ({ row }) => <CourseSyncModal row={row} onClose={closeModal} />,
      },
      {
        actionKey: 'tutorSync',
        title: 'Tutor Sync',
        render: ({ row }) => <TutorSyncModal row={row} onClose={closeModal} />,
      },
      {
        actionKey: 'allSync',

        title: 'Confirm All Sync',
        render: ({ row }) => <ConfirmAllSyncModal row={row} onClose={closeModal} />,
      },
      {
        actionKey: 'userSync',
        title: 'User Sync',
        render: () => <UserSyncModal user={selectedUser} onClose={closeModal} />,
      },
    ],
    [closeModal, selectedUser],
  );

  const activeConfig = useMemo(
    () => modalConfig.find((config) => config.actionKey === modalState.actionKey),
    [modalConfig, modalState.actionKey],
  );

  return (
    <GlobalWrapper title="Lens Manual Sync">
      {/* Page background */}
      <Box
        sx={{
          backgroundImage: 'linear-gradient(135deg, #f5f7fb 0%, #eef3ff 40%, #ffffff 100%)',
          minHeight: '100%',
          py: 2,
        }}
      >
        <Paper
          elevation={3}
          sx={{
            p: 3,
            borderRadius: 3,
            border: '1px solid',
            borderColor: 'divider',
          }}
        >
          {/* Header */}
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            justifyContent="space-between"
            alignItems={{ xs: 'flex-start', sm: 'center' }}
            spacing={2}
            mb={2}
          >
            <Box>
              <Typography variant="h6" fontWeight={600}>
                Lens Manual Sync
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Search by admission number and manage student, course, and tutor sync status.
              </Typography>
            </Box>

            <Button
              variant="contained"
              color="primary"
              onClick={() => {
                const cred = JSON.parse(localStorage.getItem('cred') || '{}');
                const profile = cred?.profile;

                if (profile) {
                  setSelectedUser({
                    name: profile.name || '',
                    mobile: profile.phone || '',
                    email: profile.email || '',
                  });

                  setModalState({
                    actionKey: 'userSync',
                    row: null,
                  });
                }
              }}
              startIcon={meta?.isUserSynced ? <CheckCircleIcon color="success" /> : <CancelIcon color="error" />}
            >
              User Sync
            </Button>
          </Stack>
          <Divider sx={{ mb: 2 }} />

          {/* <Box
            sx={{
              '& .MuiTableHead-root': {
                backgroundColor: 'grey.50',
              },
            }}
          > */}
          <Box mt={2}>
            <GlobalTable
              rowUniqueId="slNo"
              headers={LensSyncTableHeader()}
              tableState={tableState}
              setTableState={setTableState}
              rows={data}
              meta={meta}
              loading={loading}
              onButtonActionClick={handleButtonActionClick}
            />
          </Box>
          <Box>
            {/* ♻️ Dialog */}
            <GlobalDialog
              title={activeConfig?.title}
              open={Boolean(modalState?.actionKey)}
              setOpen={(val) => {
                if (!val) setModalState({ actionKey: '', row: null });
              }}
              closeButton={true}
              content={activeConfig?.render?.({ row: modalState.row })}
              maxWidth="sm"
              fullWidth
            />
          </Box>
        </Paper>
      </Box>
    </GlobalWrapper>
  );
}
